import React from "react";
import PropTypes from "prop-types";
import { NodeSelect, NodeTextField } from "../../../../NodeUI";
import {
  MISSION_DURATION_UNITS,
  composeMissionDuration,
  parseMissionDuration,
} from "./missionPolicyFormat";
import "./MissionHarness.css";

/**
 * Duration control: number + unit (ManagedRunPolicy layout). Read-only by default (template
 * preview); pass {@code disabled=false} + {@code onChange(isoValue)} to edit a per-session policy.
 * {@code inline} renders amount + unit as sibling grid cells (parent supplies the row grid).
 */
export default function MissionReadOnlyDurationField({
  label,
  isoValue,
  fallbackUnit = "hours",
  helperText,
  disabled = true,
  onChange,
  inDialog = false,
  inline = false,
}) {
  const parsed = parseMissionDuration(isoValue, fallbackUnit);

  const emit = (amount, unit) => {
    if (onChange) onChange(composeMissionDuration(amount, unit));
  };

  const amountField = (
    <NodeTextField
      label={label}
      type="number"
      value={String(parsed.amount)}
      disabled={disabled}
      onChange={(v) => emit(v, parsed.unit)}
      inputProps={{ min: 1, readOnly: disabled }}
    />
  );

  const unitField = (
    <NodeSelect
      label="Unit"
      value={parsed.unit}
      options={MISSION_DURATION_UNITS}
      disabled={disabled}
      inDialog={inDialog}
      onChange={(u) => emit(parsed.amount, u)}
    />
  );

  if (inline) {
    return (
      <>
        {amountField}
        {unitField}
      </>
    );
  }

  return (
    <div className="mission-harness__duration-field">
      <div className="mission-harness__duration-row">
        {amountField}
        {unitField}
      </div>
      {parsed.raw ? (
        <p className="mission-harness__helper">
          Stored as {parsed.raw}. Preview shows the nearest amount and unit.
        </p>
      ) : null}
      {helperText ? <p className="mission-harness__helper">{helperText}</p> : null}
    </div>
  );
}

MissionReadOnlyDurationField.propTypes = {
  label: PropTypes.string,
  isoValue: PropTypes.string,
  fallbackUnit: PropTypes.string,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  inDialog: PropTypes.bool,
  inline: PropTypes.bool,
};
